import { jwtDecode } from 'jwt-decode'

import { apiFetch } from '@/utils/api'

/**
 * Public (unauthenticated) token for the pay-with-OCR flow reachable from the login screen
 * (ADA-4432 D3). Replaces the legacy `api_mixin.get_public_token()` + Vuex `public_token`.
 *
 * The public endpoints take this token as a PATH SEGMENT or a BODY field — never as the
 * `x-api-key` header, which `utils/api.ts` attaches only for the signed-in customer token.
 * It is therefore deliberately kept OUT of `authStore`: a token that ended up there would be
 * sent as `x-api-key` on authenticated calls.
 *
 * The server mints these with a 60-second TTL, so the module-level cache is a short reuse
 * window for one payment flow — not a session. State is module-scoped (not per-component)
 * because it is a shared credential: two callers must not mint two tokens in parallel.
 */

/** Refresh this long before `exp` so a token cannot expire mid-request. */
const EXPIRY_MARGIN_MS = 5000

let cachedToken: string | null = null
let inFlight: Promise<string> | null = null
// Bumped by clearPublicToken(): a mint that STARTED before the clear must not repopulate the
// cache when it resolves after it — the flow that requested it no longer exists (PR #897 review).
let generation = 0

function isUsable(token: string | null): token is string {
  if (token == null) return false
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token)
    // No exp means we cannot reason about the lifetime — treat it as unusable rather than
    // risk sending an expired token.
    if (exp == null) return false
    return exp * 1000 - EXPIRY_MARGIN_MS > Date.now()
  } catch {
    return false
  }
}

async function mint(): Promise<string> {
  const startedGeneration = generation
  const { data } = await apiFetch.request('post', '/authenticates/public-user/{application}', {
    params: { path: { application: import.meta.env.VITE_APP_NAME } },
  })
  const token = data?.token
  if (token == null) throw new Error('Public token request returned no token')
  if (startedGeneration === generation) cachedToken = token
  return token
}

export function usePublicToken() {
  /** A valid public token, minting one only when the cached token is missing or about to expire. */
  async function getPublicToken(): Promise<string> {
    if (isUsable(cachedToken)) return cachedToken
    // Concurrent callers (the OCR lookup and the payment POST can overlap) share one mint —
    // otherwise the second request would race the first and discard its token.
    if (inFlight == null) {
      const pending = mint().finally(() => {
        // Deregister only our own promise — clearPublicToken() may have dropped it already
        // and a NEWER mint may occupy the slot; settling late must not tear that one down.
        if (inFlight === pending) inFlight = null
      })
      inFlight = pending
    }
    return await inFlight
  }

  /** Drop the cached token (auth transitions — the public flow must not span identities). */
  function clearPublicToken() {
    generation++
    cachedToken = null
    inFlight = null
  }

  return { getPublicToken, clearPublicToken }
}
